import React from 'react';
import c from 'classnames';

import { SearchkitWrapper } from '@uncinc/react-searchkit';
import type { SearchkitWrapperProps } from '@uncinc/react-searchkit/dist/components/Wrapper';

import { APP_OPTIONS, SEARCHKIT_HOST_URL } from '../../config/constants';
import {
  highlightAttributes,
  resultAttributes,
  searchAttributes,
  sorting,
} from './config';

// Subcomponents.
import Results from './Results';
import Stats from './Stats';
import Pagination from './Pagination';

interface SearchForumCategoryPostsSubcomponents {
  Results: typeof Results;
  Stats: typeof Stats;
  Pagination: typeof Pagination;
}

export interface SearchForumCategoryPostsProps extends Omit<SearchkitWrapperProps, 'connection' | 'indexName' | 'routing' | 'analytics' | 'hitsPerPage' | 'searchSettings' | 'requestOptions'> {
  tid: number; // Taxonomy term ID van de categorie
  uid?: number;
  className?: string;
}

// eslint-disable-next-line max-len
const SearchForumCategoryPosts: React.FC<SearchForumCategoryPostsProps> & SearchForumCategoryPostsSubcomponents = (props) => {
  const {
    tid,
    uid,
    className,
    children,
    ...restProps
  } = props;

  const classNames = c('SearchForumCategoryPosts', className);

  return (
    <SearchkitWrapper
      {...restProps}
      className={classNames}
      connection={{ host: SEARCHKIT_HOST_URL }}
      indexName={APP_OPTIONS.parameters.searchkitIndexForumThread}
      routing={false}
      analytics={false}
      hitsPerPage={10}
      searchSettings={{
        search_attributes: searchAttributes,
        result_attributes: resultAttributes,
        highlight_attributes: highlightAttributes,
        sorting,
      }}
      requestOptions={{
        getBaseFilters: () => [
          {
            bool: {
              must: [
                {
                  term: {
                    post_category_id: { value: tid },
                  },
                },
                {
                  term: {
                    post_status: { value: true },
                  },
                },
              ],
            },
          },
        ],
        hooks: {
          beforeSearch: (requests) => {
            const requestsWithOptions = requests;
            if (requestsWithOptions[0]) {
              // ElasticSearch track_total_hits will actually show the total hits,
              // instead of a maximum of 10.000.
              requestsWithOptions[0].body.track_total_hits = true;
            }

            requests.forEach((request) => {
              const { query } = request.body;
              delete request.body.query;

              // Boost followed posts.
              request.body.query = {
                function_score: {
                  query,
                  boost_mode: 'sum',
                  score_mode: 'sum',
                  functions: [
                    {
                      filter: {
                        terms: {
                          post_follow_uids: [uid],
                        },
                      },
                      weight: 100,
                    },
                  ],
                },
              };
            });

            return new Promise((resolve) => {
              resolve(requests);
            });
          },
        },
      }}
    >
      {children}
    </SearchkitWrapper>
  );
};

SearchForumCategoryPosts.Results = Results;
SearchForumCategoryPosts.Stats = Stats;
SearchForumCategoryPosts.Pagination = Pagination;

export default SearchForumCategoryPosts;
